import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Fieldset from '../../../components/forms/Fieldset';
import { Button } from '../../../components/buttons';
import {
	BirthDatePicker,
	// CountryDropdown,
	DropdownInput,
	Label,
	// ProvinceDropdown,
	TextInput,
	ToggleSwitch
} from '../../../components/forms';
import policyActions from '../../../policy/policyActions';
import { TooltipModal } from '../../../components/common';
import Utils, {
	UtilsBeneficiaries,
	InputError,
	Validation,
	BeneficiaryTypes
} from '../../../common/utils';
import { defaultTrustee } from '../../../policy/policyAPI';
import * as toastr from 'toastr';

interface Props {
	// redux props
	customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto;
	pendingBeneficiaries: CustomerPolicyBeneficiaryDto[];

	// parent props
	customer: UserDetails;
	customerPolicy: CustomerPolicy;
	nextStep: (e: any) => void;
	hideSkipButton: () => void;
	hidden: boolean;
	history: any;
	match?: any;
	actions: any;
}

interface State {
	touched: {
		birthDate: boolean;
		email: boolean;
		postalCode: boolean;
	};
	relationshipOther: string;
	isLoading: boolean;
}

class OptionalBeneficiaryPage extends React.Component<Props, State> {
	errors = { birthDate: {} as InputError, email: {} as InputError, postalCode: {} as InputError };
	constructor(props: Props) {
		super(props);
		this.state = {
			touched: {
				birthDate: false,
				email: false,
				postalCode: false,
			},
			relationshipOther: '',
			isLoading: false
		};
	}

	componentDidMount() {
		this.setState({ isLoading: true });
		this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.customer.id)
			.then(() => this.setState({ isLoading: false }));
	}

	saveBeneficiary = (e: any) => {
		const beneficiary = {
			...this.props.customerPolicyBeneficiary,
			customerId: this.props.customer.id,
			customerPolicyId: this.props.customerPolicy.id,
			disbursementPercent: 0
		};
		// confusing part: the if statement here is checking the scenario that user clicks on next button accidently
		// now he/she still wants to go to disbursement page and incomplete beneficiary should not be saved
		if (!beneficiary.firstName || !beneficiary.lastName || !beneficiary.birthDate) {
			this.props.hideSkipButton();
			return this.props.actions.resetCustomerPolicyBeneficiary();
		}
		return this.props.actions.saveCustomerPolicyBeneficiary(beneficiary)
			.then(() => this.props.hideSkipButton())
			.then(() => this.props.actions.resetCustomerPolicyBeneficiary())
			.then(() => toastr.success('Beneficiary saved!'));
	}

	saveSingle = (e: any) => {
		this.saveBeneficiary(e)
			.then(() => this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.customer.id))
			.then(() => {
				const filteredBeneficiaries = this.props.pendingBeneficiaries
					.filter(b => b.customerPolicyId === this.props.customerPolicy.id);
				this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);

				if (this.props.pendingBeneficiaries.some(b => b.isUnderage === true)) {
					const trustee = this.props.pendingBeneficiaries.find(b => b.typeId === BeneficiaryTypes.Trustee);
					if (trustee) {
						this.props.actions.setTrustee(trustee);
					} else {
						let newTrustee = defaultTrustee;
						this.props.actions.setTrustee(newTrustee);
					}
				}
			})
			.then(() => this.props.nextStep(e));
	}

	addMore = (e: any) => {
		this.saveBeneficiary(e)
			.then(() => this.props.actions.resetCustomerPolicyBeneficiary())
			.then(() => this.props.history.push(`/quote/${this.props.match.params.workflow}/enrollment/beneficiary`));
	}

	render() {
		this.errors = this.validate();
		const customer = { ...this.props.customer };
		return (
			<div className="content-set" hidden={this.props.hidden}>
				<Fieldset>
					<h2 className="form-fieldset-title">
						Beneficiary info
					</h2>
					<div>
						<h4  className="form-fieldset-title"><b>What is a beneficiary ?</b></h4>
						<p>
							A beneficiary is the person or people that you determine should receive your life insurance payout if you die while the policy is in effect.
						</p>
						<p>
							The section below allows you to designate one or more people as beneficiaries of your policy.
							By completing it, you’re telling us that any proceeds should go directly to them.  Note that if you name multiple beneficiaries,
							you’ll have to decide up front what portion of the proceeds should go to each person.
						</p>
						<p>
							We recommend that you complete this step.  If you decide to skip it, your beneficiary will be your estate.
						</p>
						<p>
							You can always update your beneficiary designation in the future if things change.
						</p>
					</div>
					<div className="form-fieldset">
						<div className="row">
							<TextInput
								id="beneficiaryFirstName"
								labelText="First Name"
								name="firstName"
								onChange={this.onPolicyBeneficiaryChange}
								value={this.props.customerPolicyBeneficiary.firstName}
								className="col-md-6"
								required={true}
							/>
							<TextInput
								id="beneficiaryLastName"
								labelText="Last Name"
								name="lastName"
								onChange={this.onPolicyBeneficiaryChange}
								value={this.props.customerPolicyBeneficiary.lastName}
								className="col-md-6"
								required={true}
							/>
							<div className="form-daypicker col-md-12">
								<BirthDatePicker
									id="beneficiaryDOB"
									labelText="Birthdate"
									name="birthDate"
									value={this.props.customerPolicyBeneficiary.birthDate}
									onBlur={this.handleBlur('birthDate')}
									onChange={this.onPolicyBeneficiaryChange}
									error={
										Utils.shouldMarkError(this.errors.birthDate, this.state.touched.birthDate) ? this.errors.birthDate : undefined
									}
									required={true}
								/>
							</div>
							<DropdownInput
								labelText="Relationship"
								id="relationship"
								name="relationship"
								value={this.props.customerPolicyBeneficiary.relationship}
								defaultOption="-"
								onChange={this.onRelationshipChange}
								options={[
									{
										text: 'Spouse',
										value: 'Spouse'
									},
									{
										text: 'Child',
										value: 'Child'
									},
									{
										text: 'Grandchild',
										value: 'Grandchild'
									},
									{
										text: 'Parent',
										value: 'Parent'
									},
									{
										text: 'Sibling',
										value: 'Sibling'
									},
									{
										text: 'Other - specify',
										value: 'Other'
									}
								]}
								className="col-md-6"
							/>
							{this.props.customerPolicyBeneficiary.relationship === 'Other' &&
								<TextInput
									id="relationshipOther"
									labelText=" "
									name="relationshipOther"
									onChange={this.onPolicyBeneficiaryChange}
									value={this.props.customerPolicyBeneficiary.relationshipOther}
									maxLength={20}
									className="col-md-6"
								/>
							}
							<TextInput
								id="beneficiaryEmail"
								labelText="Beneficiary Email"
								name="email"
								onBlur={this.handleBlur('email')}
								onChange={this.onPolicyBeneficiaryChange}
								value={this.props.customerPolicyBeneficiary.email}
								error={
									Utils.shouldMarkError(this.errors.email, this.state.touched.email) ? this.errors.email : undefined
								}
								className="col-md-12"
							/>

							<div className="col-md-auto form-group">
								<div className="mb-1">
									<Label className="mr-2 mb-0 strong">Send Email Notification</Label>
									<TooltipModal
										toggle="What does this mean?"
										toggleClassName="em btn-link-inline"
									>
										<h3>This is a sample of the email notification that will be sent to the beneficiary:</h3>

										<p>This email is to notify you that {customer.firstName} {customer.lastName} has purchased a life insurance policy and 
											designated you as a beneficiary. {customer.firstName} requested that we notify you about the policy in the unfortunate
											event a claim needs to be submitted.</p>
										<p>For your future reference, the policy number is <b>xx-xxxxx</b>. We are not able to share any additional details regarding the policy.</p>
										<p>To submit a claim, please visit <a href="www.insuredbymosaic.ca/claims">www.insuredbymosaic.ca/claims</a> or call us at <b>1-855-643-6945</b>.</p>
										<p>Please note that beneficiary designations may change over time and we will not notify you if you have
											been removed as a beneficiary of the policy.</p>
										<p>Thanks,</p>
										<p>The Mosaic Life Team</p>
									</TooltipModal>
								</div>
								<ToggleSwitch
									// labelText="Send notification"
									name="shouldSendNotification"
									onChildren="Yes"
									offChildren="No"
									onChange={this.onPolicyBeneficiaryChange}
									checked={this.props.customerPolicyBeneficiary.shouldSendNotification}
									className="mr-2 mb-0 toggle-button-wrap"
									disabled={Utils.isEmpty(this.props.customerPolicyBeneficiary.email)}
								/>
							</div>
							<div className="col-md-auto form-group">
								<div className="mb-1">
									<Label className="mr-2 mb-0 strong">Revocability</Label>
									<TooltipModal toggle="What does this mean?" toggleClassName="em btn-link-inline">
										<p>
											If you designate a beneficiary as irrevocable, you cannot change ("revoke")
											the claim benefits of that beneficiary without their signature. If the irrevocable beneficiary is a minor, you
											cannot  change or revoke the beneficiary until minor reaches the age of majority.
										</p>
										<p>
											Note: In Quebec, the designation of a spouse is irrevocable unless you check the revocable box
											next to their name.
										</p>
									</TooltipModal>
								</div>
								<ToggleSwitch
									name="isRevocable"
									onChildren="Revocable"
									offChildren="Irrevocable"
									onChange={this.onPolicyBeneficiaryChange}
									checked={this.props.customerPolicyBeneficiary.isRevocable}
									className="mr-2 mb-0 toggle-buttons-wrap"
								/>
							</div>
							<div className="col-md-auto form-group">
								<div className="mb-1">
									<Label className="mr-2 mb-0 strong">Primary/Contingent</Label>
									<TooltipModal toggle="What does this mean?" toggleClassName="em btn-link-inline">
										<p>
											The primary beneficiary receives the benefits payable when the insured dies.
											If more than one primary beneficiary is appointed, proceeds will be payable as per the percentages you
											indicated. Note that the sum of percentages assigned to each beneficiary must total to 100%.
										</p>
										<p>
											If multiple primary beneficiaries are designated, but no percentage allocation has been indicated, benefits
											will be paid out in equal shares. If no beneficiaries have been indicated, benefits from the insurance policy
											will be paid to the insured's estate.
										</p>
										<p>
											In the event that all primary beneficiaries die before the insured, the
											contingent beneficiaries will receive the benefits payable when the insured dies.
										</p>
									</TooltipModal>
								</div>
								<ToggleSwitch
									name="typeId"
									onChildren="Primary"
									offChildren="Contingent"
									onChange={this.onPolicyBeneficiaryChange}
									checked={this.props.customerPolicyBeneficiary.typeId === BeneficiaryTypes.Primary}
									className="mr-2 mb-0 toggle-buttons-wrap"
								/>
							</div>
						</div>
					</div>
					
					<div className="mt-3">
						<Button
							className="btn-primary mt-3 mr-3"
							onClick={this.addMore}
							disabled={this.isSubmitDisabled()}
						>
							<span className="align-middle mx-2">Add Beneficiary</span>
						</Button>
						<Button
							className="btn-primary mt-3 mr-3"
							onClick={this.saveSingle}
							disabled={this.isSubmitDisabled() && !this.hasPendingBeneficiaries()}
						>
							<span className="align-middle mx-2">Next</span>
						</Button>
					</div>
				</Fieldset>
			</div>
		);
	}

	handleBlur = (field: any) => (evt: any) => {
		this.setState({
			touched: { ...this.state.touched, [field]: true }
		});
		if (field === 'birthDate') {
			const underageChangeEvent = {
				target: {
					name: 'isUnderage',
					value: Utils.getAgeFromBirthdate(this.props.customerPolicyBeneficiary.birthDate) < 18,
					type: 'custom'
				}
			};
			this.onPolicyBeneficiaryChange(underageChangeEvent);
		}
	}

	validate = () => {
		// true means invalid
		return {
			birthDate: Validation.validateDate(this.props.customerPolicyBeneficiary.birthDate),
			email: Validation.validateEmail(this.props.customerPolicyBeneficiary.email),
			postalCode: Validation.validatePostalCode(this.props.customerPolicyBeneficiary.postalCode)
		};
	}

	isSubmitDisabled = () => {
		if (this.errors.birthDate.hasError || this.errors.email.hasError || this.errors.postalCode.hasError) {
			return true;
		}

		const { firstName, lastName, birthDate } = this.props.customerPolicyBeneficiary;
		return !firstName || !lastName || !birthDate;
	}

	hasPendingBeneficiaries = () => {
		return this.props.pendingBeneficiaries.length > 0;
	}

	onPolicyBeneficiaryChange = (e: any) => {
		let value = e.target.value;
		if (e.target.type === 'radio') {
			if (e.target.name === 'typeId') {
				if (value === 'true') {
					value = BeneficiaryTypes.Primary;
				} else if (value === 'false') {
					value = BeneficiaryTypes.Contingent;
				}
			} else {
				if (value === 'true') {
					value = true;
				} else if (value === 'false') {
					value = false;
				}
			}
		}
		const customerPolicyBeneficiary = {
			...this.props.customerPolicyBeneficiary,
			[e.target.name]: value
		};
		this.props.actions.setCustomerPolicyBeneficiary(customerPolicyBeneficiary);
	}

	onRelationshipChange = (e: any) => {
		let value = e.target.value;
		const customerPolicyBeneficiary = {
			...this.props.customerPolicyBeneficiary,
			[e.target.name]: value
		};

		if (e.target.value !== 'Other') {
			customerPolicyBeneficiary.relationshipOther = '';
		}

		this.props.actions.setCustomerPolicyBeneficiary(customerPolicyBeneficiary);
	}
}

function mapStateToProps(state: any) {
	const pendingBeneficiaries = state.policy.beneficiaries.pending
		.filter((bene: CustomerPolicyBeneficiaryDto) => bene.customerPolicyId === state.policy.customerPolicy.id)
		.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	return {
		customerPolicyBeneficiary: state.policy.customerPolicyBeneficiary,
		pendingBeneficiaries
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default connect(mapStateToProps, mapDispatchToProps)(OptionalBeneficiaryPage);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/enrollment/billing-review/OptionalBeneficiaryPage.tsx